import { createFileRoute } from "@tanstack/react-router";
import { isId } from "shared/Ids";
import { isUuid } from "shared/SharedUtils";
import { type LazyModuleKey, lazyComponent, preloadLazyModule } from "src/lib/LazyModules";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { getAdminPageData } from "src/server/functions/Admin";

const AdminDashboard = lazyComponent("adminDashboard", "default");

// `?tab=` names the open back office section (any string, as before; the default is "tickets").
export interface AdminSearch {
  tab?: string;
  userId?: string;
  caseId?: string;
  ticketId?: string;
  assetId?: string;
  exerciseAssetId?: string;
  noticeId?: string;
  linkKind?: string;
  linkId?: string;
}

/** The lazy module (src/lib/LazyModules.tsx) of each tab AdminDashboard renders. */
const ADMIN_TAB_MODULES = new Map<string, LazyModuleKey>([
  ["compliance", "adminCompliance"],
  ["analytics", "adminAnalytics"],
  ["tickets", "adminSupportTickets"],
  ["safety-holds", "adminSafetyHolds"],
  ["users", "adminUsers"],
  ["projects", "adminProjects"],
  ["organisations", "adminOrganisations"],
  ["waitlist", "adminWaitlist"],
  ["subprocessors", "adminSubprocessors"],
]);

// The parent route verifies system-admin access before this loader fetches the first page
// (`getAdminPageData`) and code so it renders with the document; switching tabs loads the next one.
export const Route = createFileRoute("/_authed/admin/")({
  validateSearch: (search: Record<string, unknown>): AdminSearch => ({
    ...(isId(search.assetId, "asset") ? { assetId: search.assetId } : {}),
    ...(isId(search.exerciseAssetId, "asset") ? { exerciseAssetId: search.exerciseAssetId } : {}),
    // Subprocessor notices keep internal UUIDs; a compliance link names an asset or a notice.
    ...(typeof search.noticeId === "string" && isUuid(search.noticeId)
      ? { noticeId: search.noticeId }
      : {}),
    ...(isId(search.linkId, "asset") || (typeof search.linkId === "string" && isUuid(search.linkId))
      ? { linkId: search.linkId as string }
      : {}),
    ...(["safety-hold", "subprocessor-notice"].includes(search.linkKind as string)
      ? { linkKind: search.linkKind as string }
      : {}),
    ...(isId(search.caseId, "case") ? { caseId: search.caseId } : {}),
    ...(isId(search.ticketId, "tkt") ? { ticketId: search.ticketId } : {}),
    ...(typeof search.tab === "string" && search.tab ? { tab: search.tab } : {}),
    ...(typeof search.userId === "string" && search.userId.length <= 256
      ? { userId: search.userId }
      : {}),
  }),
  loaderDeps: ({ search }) => ({
    tab: search.tab ?? "tickets",
    userId: search.userId,
    caseId: search.caseId,
    assetId: search.assetId,
    noticeId: search.noticeId,
    linkId: search.linkId,
    linkKind: search.linkKind,
    ticketId: search.ticketId,
  }),
  // Each tab's code is a chunk of its own; the open tab's loads with its data.
  loader: async ({ deps }) => {
    const tabModule = ADMIN_TAB_MODULES.get(deps.tab);
    const [result] = await Promise.all([
      raceLoaderData(getAdminPageData({ data: deps })),
      preloadLazyModule("adminDashboard"),
      tabModule ? preloadLazyModule(tabModule) : undefined,
    ]);
    return result;
  },
  component: AdminPage,
});

function AdminPage() {
  const { tab, userId, caseId, assetId, noticeId, linkId, linkKind, ticketId } = Route.useSearch();
  return (
    // Each tab has its own data: a new tab starts from its own (or its loading state), not the last.
    <DeferredLoaderData
      key={`${tab ?? "tickets"}:${userId ?? ""}:${caseId ?? ""}:${assetId ?? ""}:${noticeId ?? ""}:${linkKind ?? ""}:${linkId ?? ""}:${ticketId ?? ""}`}
      result={Route.useLoaderData()}
    >
      {(initialData) => <AdminDashboard activeTab={tab ?? "tickets"} initialData={initialData} />}
    </DeferredLoaderData>
  );
}
